import React, { useCallback, useContext, useMemo } from 'react';
import {
  FreeProductsModalProps,
  GlobalAppContextMenuInterface,
  LoginModalProps,
  OpeningHoursScheduleInterface,
  OrderBasketModalProps,
  OrderMethodModalProps,
  OtpVerificationModalProps,
  RestaurantClosedModalProps,
} from '../services/exports/Interfaces';
import { APP_STATE_REDUCER_TYPES } from '../services/exports/Constants';
import { ConfirmCancelModalProps } from '../modals/ConfirmCancelModal';
import { FormatCurrencyOptions, MenuItemModalProps } from '../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import HelperMethods from '../services/exports/HelperMethods';
import moment, { Moment } from 'moment-timezone';
import PickUpTimesResource from '../services/resources/PickUpTimesResource';
import collect from 'collect.js';
import { TFunction } from 'i18next';
import { ValidationErrorResponseData } from '../services/exports/Types';
import { AppStateContext } from './AppStateProvider';
import { Modals } from '../modules/mobile/exports/Enums';
import useModule from '../hooks/utility/useModule';
import { modalRouterRef } from '../modules/mobile/router/ModalRouter';

interface Props {
  children: any;
}

export interface GlobalAppMethodsContextInterface {
  toggleLoginModal: (payload?: Partial<LoginModalProps>) => void;
  toggleInfoModal: (payload: ConfirmCancelModalProps) => void;
  toggleMenuItemModal: (payload: MenuItemModalProps) => void;
  saveMenu: (payload: Partial<GlobalAppContextMenuInterface>) => void;
  toggleOrderMethodModal: (payload: Partial<OrderMethodModalProps>) => void;
  toggleOtpVerificationModal: (payload?: Partial<OtpVerificationModalProps>) => void;
  toggleFreeProductsModal: (payload: Partial<FreeProductsModalProps>) => void;
  toggleRestaurantClosedModal: (payload: Partial<RestaurantClosedModalProps>) => void;
  toggleOrderBasketModal: (payload: Partial<OrderBasketModalProps>) => void;
  formatCurrency: (amount: number, options?: FormatCurrencyOptions) => number | string;
  formatTime: (datetime: string | Moment, format?: undefined | string) => string;
  formatDateTime: (
    datetime: string | Moment,
    format?: {
      format?: undefined | string;
      dateFormat?: undefined | string;
      timeFormat?: undefined | string;
    },
  ) => string;
  formatScheduleUnit: (
    scheduleUnit: Moment | string | OpeningHoursScheduleInterface,
    format?: {
      prefix?: string;
      day?: boolean;
      time?: boolean;
    },
  ) => string;
  createScheduledOrderSlot: (time: string | Moment) => string;
  flattenValidationErrors: (field: string, validationErrors: ValidationErrorResponseData, t: TFunction) => string[];
}

export const AppMethodsContext: React.Context<GlobalAppMethodsContextInterface> = React.createContext(null);

export default function AppMethodsProvider(props: Props) {
  const { children } = props;

  const { state, dispatch } = useContext(AppStateContext);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { executeModule } = useModule();

  const formatTime = useCallback<(datetime: string | Moment, format?: undefined | string) => string>(
    (datetime, format = undefined) => {
      const _format = format ?? (company?.country === 'US' ? 'h:mm a' : 'HH:mm');

      return moment(datetime).format(_format);
    },
    [company?.country],
  );

  const formatDateTime = useCallback<
    (
      datetime: string | Moment,
      format: { format?: undefined | string; dateFormat?: undefined | string; timeFormat?: undefined | string },
    ) => string
  >(
    (datetime: string | Moment, { format = undefined, dateFormat = undefined, timeFormat = undefined } = {}) => {
      const _dateFormat = dateFormat ?? 'DD.MM.YYYY';
      const _timeFormat = timeFormat ?? (company?.country === 'US' ? 'h:mm a' : 'HH:mm');
      const _format = format ?? `${_dateFormat} ${_timeFormat}`.trim();

      return moment(datetime).format(_format);
    },
    [company?.country],
  );

  const formatScheduleUnit = useCallback(
    (
      scheduleUnit: Moment | string | OpeningHoursScheduleInterface,
      { prefix = undefined, day = undefined, time = true } = {},
    ): string =>
      (
        (prefix ?? '') +
        ' ' +
        formatDateTime(
          typeof scheduleUnit === 'string' || moment.isMoment(scheduleUnit)
            ? moment(scheduleUnit)
            : PickUpTimesResource.createDate(scheduleUnit),
          {
            dateFormat: day ? 'dddd' : '',
            timeFormat: time ? undefined : '',
          },
        )
      ).trim(),
    [formatDateTime],
  );

  const context: GlobalAppMethodsContextInterface = useMemo(
    () => ({
      toggleLoginModal: (payload: LoginModalProps) => {
        if (!payload) {
          return dispatch({
            type: APP_STATE_REDUCER_TYPES.TOGGLE_LOGIN_MODAL,
            payload: {
              loginModalProps: {
                openModal: !state.loginModalProps.openModal,
                onCloseRedirect: () => null,
              },
            },
          });
        }

        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_LOGIN_MODAL,
          payload: {
            loginModalProps: payload,
          },
        });
      },
      toggleInfoModal: (payload: ConfirmCancelModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_INFO_MODAL,
          payload: {
            infoModalProps: payload,
          },
        });
      },
      toggleMenuItemModal: (payload?: MenuItemModalProps) => {
        if (!payload) {
          return dispatch({
            type: APP_STATE_REDUCER_TYPES.TOGGLE_MENU_ITEM_MODAL,
            payload: {
              menuItemModalProps: {
                openModal: false,
                product: null,
                lineItem: null,
              },
            },
          });
        }

        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_MENU_ITEM_MODAL,
          payload: {
            menuItemModalProps: payload,
          },
        });
      },
      toggleFreeProductsModal: (payload: FreeProductsModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_FREE_PRODUCTS_MODAL,
          payload: {
            freeProductsModalProps: payload,
          },
        });
      },
      saveMenu: (data: GlobalAppContextMenuInterface) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.SAVE_MENU,
          payload: {
            menu: data,
          },
        });
      },
      toggleOrderMethodModal: (payload: OrderMethodModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_METHOD_MODAL,
          payload: {
            orderMethodModalProps: payload,
          },
        });
        executeModule({
          mobile: () =>
            payload.openModal
              ? modalRouterRef.current?.openModal({
                  id: Modals.OrderMethod,
                  props: payload,
                })
              : modalRouterRef.current?.closeModal(),
        });
      },
      toggleOtpVerificationModal: (payload?: OtpVerificationModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_OTP_VERIFICATION_MODAL,
          payload: {
            otpVerificationModalProps: payload ?? {
              openModal: false,
            },
          },
        });
      },
      toggleRestaurantClosedModal: (payload: RestaurantClosedModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_RESTAURANT_CLOSED_MODAL,
          payload: {
            restaurantClosedModalProps: payload,
          },
        });
      },
      toggleOrderBasketModal: (payload: OrderBasketModalProps) => {
        dispatch({
          type: APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_BASKET_MODAL,
          payload: {
            orderBasketModalProps: payload,
          },
        });
      },
      formatCurrency: (amount: number, options?: FormatCurrencyOptions): number | string =>
        HelperMethods.formatCurrency(amount, {
          ...options,
          currency: options?.currency ?? company?.currency ?? 'EUR',
        }),
      formatTime,
      formatDateTime,
      formatScheduleUnit,
      createScheduledOrderSlot: (time: Moment | string): string => {
        return (
          formatTime(time) +
          ' - ' +
          formatTime(moment(time).add(company?.scheduled_orders_time_slot_interval, 'minutes'))
        );
      },
      flattenValidationErrors: (field: string, validationErrors: ValidationErrorResponseData, t: TFunction): string[] =>
        collect(validationErrors?.rules[field] ?? [])
          .map((attributes, rule) =>
            t(`validation.${field}.${rule}`, { attributes, defaultValue: validationErrors?.errors[field] }),
          )
          .toArray(),
    }),
    [formatTime, formatDateTime, formatScheduleUnit, company?.currency, company?.scheduled_orders_time_slot_interval],
  );

  return <AppMethodsContext.Provider value={context}>{children}</AppMethodsContext.Provider>;
}
